import { type ChangeEvent, type KeyboardEvent, memo, type Ref, useCallback } from 'react';

import { getWorkspaceAgentSelectAssetActiveOptionId } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/getWorkspaceAgentSelectAssetOptionId';
import { useWorkspaceAgentSelectAssetSearchKeyDown } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/useWorkspaceAgentSelectAssetSearchKeyDown';
import { WorkspaceAgentSelectAssetSearchInput } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetSearchInput';
import {
  useWorkspaceAgentSelectAssetStore,
  type WorkspaceAgentSelectAssetStoreState,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/WorkspaceAgentSelectAssetStoreContext';
import { type WorkspaceAgentSelectAssetResults } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/workspaceAgentSelectAssetTypes';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';

const BOARD_PICKER_SEARCH_PLACEHOLDER = 'Search workspace';

const getBoardHighlightedIndex = (state: WorkspaceAgentSelectAssetStoreState) => state.boardHighlightedIndex;

const getBoardResults = (state: WorkspaceAgentSelectAssetStoreState) => state.boardResults;

const handleBoardPickerSearchEscape = (
  event: KeyboardEvent<HTMLElement>,
  state: WorkspaceAgentSelectAssetStoreState,
) => {
  event.preventDefault();
  state.setBoardPickerOpen(false);
};

const handleBoardPickerHorizontalArrow = (
  event: KeyboardEvent<HTMLElement>,
  results: WorkspaceAgentSelectAssetResults,
  highlightedIndex: number,
) => {
  if (!results.onToggleExpanded) {
    return false;
  }

  const item = results.items[highlightedIndex];
  const shouldExpand = event.key === 'ArrowRight';

  if (item && isWorkspaceAgentMentionTreeItem(item) && item.hasChildren && item.isExpanded !== shouldExpand) {
    event.preventDefault();
    results.onToggleExpanded(item.id, item.isExpanded);
  }

  return true;
};

export type WorkspaceAgentSelectAssetBoardPickerSearchFieldProps = {
  inputRef?: Ref<HTMLInputElement>;
};

export const WorkspaceAgentSelectAssetBoardPickerSearchField = memo(
  ({ inputRef }: WorkspaceAgentSelectAssetBoardPickerSearchFieldProps) => {
    const query = useWorkspaceAgentSelectAssetStore((state) => state.boardPickerQuery);
    const setQuery = useWorkspaceAgentSelectAssetStore((state) => state.setBoardPickerQuery);
    const moveHighlight = useWorkspaceAgentSelectAssetStore((state) => state.moveBoardHighlight);
    const activeOptionId = useWorkspaceAgentSelectAssetStore((state) =>
      getWorkspaceAgentSelectAssetActiveOptionId(state.boardResults.items, state.boardHighlightedIndex),
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
      },
      [setQuery],
    );

    const handleClear = useCallback(() => {
      setQuery('');
    }, [setQuery]);

    const { handleKeyDown } = useWorkspaceAgentSelectAssetSearchKeyDown({
      getHighlightedIndex: getBoardHighlightedIndex,
      getResults: getBoardResults,
      moveHighlight,
      onEscape: handleBoardPickerSearchEscape,
      onHorizontalArrow: handleBoardPickerHorizontalArrow,
    });

    return (
      <WorkspaceAgentSelectAssetSearchInput
        activeOptionId={activeOptionId}
        inputRef={inputRef}
        onChange={handleChange}
        onClear={handleClear}
        onKeyDown={handleKeyDown}
        placeholder={BOARD_PICKER_SEARCH_PLACEHOLDER}
        query={query}
      />
    );
  },
);

WorkspaceAgentSelectAssetBoardPickerSearchField.displayName = 'WorkspaceAgentSelectAssetBoardPickerSearchField';
